import {config, createAppContext, EnforcedLanguageSetting} from './context';
import {Translate} from '@planday-corp/globalization';
import {AppProvider} from '@planday-corp/react-app';
import ToastManager from '@planday-corp/react-app/components/ToastManager';
import ToastProvider from '@planday-corp/react-app/components/ToastProvider';
import CSSReset from '@planday-corp/ui/global/CSSReset';
import Fonts from '@planday-corp/ui/global/Fonts';
import UIProvider from '@planday-corp/ui/global/UIProvider';
import {grey05} from '@planday-corp/ui/styles/colors';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import React, {PropsWithChildren, Suspense, useMemo} from 'react';
import {createGlobalStyle} from 'styled-components';

// Soft export of types
// It's important that only types are exported to avoid breaking code splitting
export type AppContext = ReturnType<typeof createAppContext>;
export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// This is a workaround specifically for PlandaySignup, not to affect current XeroSignup behavior
// In XeroSignup we're using instance of Api class, but it cannot be instantiated without correct serviceUrl
// In PlandaySignup we need to be able to call BE (self) on any dynamic environment, and not stick to hardcoded staging configuration
// To achieve that we're utilizing fetch API, and calling root path directly
// For full development experience (for .NET devs) frontend should, by default call "https://localhost:5738"

export const SIGNUP_API_URL = IS_DEVELOPMENT
  ? config.get('internalServices.signupApiUrl')
  : '';

const GlobalStyles = createGlobalStyle`
    body, html, #root {
        height: 100%;
        margin: 0;
        overflow-y: auto;
        background-color: ${grey05};
    }
`;

interface AppProps {
  enforcedLanguage?: EnforcedLanguageSetting;
}

export default function App(props: PropsWithChildren<AppProps>) {
  const context = useMemo(
    () => createAppContext(props.enforcedLanguage),
    [props.enforcedLanguage]
  );

  return (
    <Suspense
      fallback={
        <Translate term="CommonTerms.LoadingPleaseWait" fallback="Loading..." />
      }
    >
      <UIProvider>
        <GlobalStyles />
        <CSSReset />
        <Fonts />
        <AppProvider
          services={context}
          style={{height: '100%'}}
          useExperimentalIntl
        >
          <QueryClientProvider client={context.queryClient}>
            {props.children}
            <ToastProvider>
              <ToastManager maxToasts={3} />
            </ToastProvider>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </AppProvider>
      </UIProvider>
    </Suspense>
  );
}
